import { Component, type ReactNode } from 'react'
import { Image, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { ScreenContainer } from './features/shared/ScreenContainer'
import { Button } from '@chillout/ui'
import { images } from './assets'

function ErrorScreen({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation()
  return (
    <ScreenContainer className="items-center justify-center px-8">
      <View className="w-full max-w-[420px] items-center self-center">
        <Image
          source={images.errorState}
          style={{ width: 180, height: 180 }}
          resizeMode="contain"
          accessibilityLabel={t('error.imageAccessibilityLabel')}
        />
        <Text className="mt-6 text-center text-3xl font-bold text-primary">
          {t('error.title')}
        </Text>
        <Text className="mt-3 text-center text-base font-normal text-secondary">
          {t('error.subtitle')}
        </Text>
        <View className="mt-10 w-full">
          <Button label={t('error.tryAgain')} onPress={onReset} />
          <Text className="mt-6 text-center text-sm font-normal text-muted">
            {t('error.footer')}
          </Text>
        </View>
      </View>
    </ScreenContainer>
  )
}

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error)
  }

  private handleReset = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      return <ErrorScreen onReset={this.handleReset} />
    }
    return this.props.children
  }
}